/**
 * McpServersSection — MCP server management UI for the Settings page.
 *
 * Shows built-in servers (cowork, nbyula) in a read-only section and
 * user-added servers in an editable section with add/edit/delete/discover.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMcpStore } from "@/stores/mcpStore";
import { Button } from "@/components/common/Button";
import type { McpServer, McpTool, McpbManifest, McpbUserConfigField } from "@/api/types";
import { apiFetch, isNativeHost } from "@/api/client";

// ── Status indicator ─────────────────────────────────────────────────

function StatusDot({ status }: { status: McpServer["status"] }) {
  const color =
    status === "connected" || status === "running"
      ? "bg-emerald-500"
      : status === "failed"
        ? "bg-red-400"
        : status === "requires_setup"
          ? "bg-amber-400"
          : "bg-space-40";
  return <span className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${color}`} />;
}

/** Short human label for a connector's status, so a user can tell at a glance
 *  which MCPs actually work (e.g. in the cloud). */
function statusLabel(status: McpServer["status"], enabled: boolean): { text: string; cls: string } | null {
  if (!enabled) return { text: "Disabled", cls: "text-space-40" };
  switch (status) {
    case "connected":
    case "running":
      return { text: "Connected", cls: "text-emerald-600" };
    case "failed":
      return { text: "Error", cls: "text-red-500" };
    case "requires_setup":
      return { text: "Desktop-only", cls: "text-amber-600" };
    default:
      return null;
  }
}

// ── Secret input with show/hide toggle ───────────────────────────────

function SecretInput({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={`relative ${className ?? ""}`}>
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-2 pr-7 py-1.5 border border-space-30 rounded-lg text-xs font-mono
                   focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                   placeholder:text-space-40"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide value" : "Show value"}
        title={visible ? "Hide value" : "Show value"}
        className="absolute inset-y-0 right-1.5 flex items-center text-space-40 hover:text-space transition-colors"
      >
        {visible ? (
          // eye-off
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.243 4.243L9.88 9.88" />
          </svg>
        ) : (
          // eye
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        )}
      </button>
    </div>
  );
}

function transportLabel(transport: string) {
  switch (transport) {
    case "sdk": return "In-process";
    case "stdio": return "stdio";
    case "http": return "HTTP";
    case "sse": return "SSE";
    default: return transport;
  }
}

// ── Tool list (expandable) ───────────────────────────────────────────

function ToolList({
  tools,
  serverId,
  readonly,
}: {
  tools: McpTool[];
  serverId: string;
  readonly?: boolean;
}) {
  const { toggleTool } = useMcpStore();
  const [toggling, setToggling] = useState<string | null>(null);

  if (tools.length === 0) {
    return <p className="text-xs text-space-40 italic">No tools discovered yet</p>;
  }

  return (
    <div className="space-y-1 max-h-48 overflow-y-auto">
      {tools.map((t) => (
        <label
          key={t.name}
          className="flex items-start gap-2 py-1 px-1 rounded hover:bg-space-10 cursor-pointer text-xs"
        >
          <input
            type="checkbox"
            checked={t.enabled}
            disabled={readonly || toggling === t.name}
            onChange={async () => {
              setToggling(t.name);
              try {
                await toggleTool(serverId, t.name, !t.enabled);
              } finally {
                setToggling(null);
              }
            }}
            className="mt-0.5 rounded border-space-30 text-jove focus:ring-jove-60/30"
          />
          <span className="flex-1">
            <span className="font-mono text-space">{t.name}</span>
            {t.description && (
              <span className="text-space-40 ml-1.5">— {t.description}</span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}

// ── Server card ──────────────────────────────────────────────────────

function ServerCard({
  server,
  onEdit,
  onDelete,
  onConfigure,
}: {
  server: McpServer;
  onEdit?: () => void;
  onDelete?: () => void;
  onConfigure?: () => void;
}) {
  const { discoverTools, verifyServer, updateServer, restartServer } = useMcpStore();
  const [expanded, setExpanded] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const handleDiscover = async () => {
    setDiscovering(true);
    setMessage(null);
    try {
      const res = await discoverTools(server.id);
      setMessage({
        ok: res.success,
        text: res.success
          ? `Discovered ${res.tools.length} tools`
          : res.error || "Discovery failed",
      });
      if (res.success) setExpanded(true);
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setDiscovering(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setMessage(null);
    try {
      const res = await verifyServer(server.id);
      setMessage({ ok: res.verified, text: res.message });
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setVerifying(false);
    }
  };

  const handleRestart = async () => {
    setRestarting(true);
    setMessage(null);
    try {
      const res = await restartServer(server.id);
      setMessage({
        ok: res.ok,
        text: res.ok ? "Server restarted" : res.message || "Restart failed",
      });
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setRestarting(false);
    }
  };

  const handleToggleEnabled = async () => {
    await updateServer(server.id, { enabled: !server.enabled });
  };

  const sl = statusLabel(server.status, server.enabled);
  // Local (stdio/extension) connectors can't run in the cloud — dim the card and
  // mark it unsupported (decided client-side from the host, so it's reliable
  // even before the server's status refresh runs).
  const unsupported =
    !isNativeHost() && !server.builtin && (server.transport === "stdio" || server.transport === "mcpb");

  return (
    <div className={`rounded-xl border border-space-30 bg-surface overflow-hidden ${unsupported ? "opacity-60" : ""}`}>
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <StatusDot status={server.enabled ? server.status : "disabled"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-space truncate">
              {server.transport === "mcpb"
                ? (server.config as Record<string, string>).display_name || server.name
                : server.name}
            </p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              server.builtin
                ? "bg-jove-60/10 text-jove"
                : server.transport === "mcpb"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-space-20 text-space-50"
            }`}>
              {server.builtin ? "Built-in" : server.transport === "mcpb" ? "Extension" : "Custom"}
            </span>
            <span className="text-[10px] text-space-40 font-mono">
              {server.transport === "mcpb"
                ? `v${(server.config as Record<string, string>).version || "?"}`
                : transportLabel(server.transport)}
            </span>
            {unsupported ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                Unsupported in cloud
              </span>
            ) : sl ? (
              <span className={`text-[10px] font-medium ${sl.cls}`}>{sl.text}</span>
            ) : null}
          </div>
          {server.transport === "mcpb" && (server.config as Record<string, string>).description && (
            <p className="text-xs text-space-40 mt-0.5 truncate">
              {(server.config as Record<string, string>).description}
            </p>
          )}
          {server.status_msg && (server.status === "failed" || server.status === "requires_setup") && (
            <p className={`text-xs mt-0.5 whitespace-pre-wrap break-words ${
              server.status === "requires_setup" ? "text-space-40" : "text-hydra"
            }`}>
              {server.status_msg}
            </p>
          )}
        </div>

        {/* Tool count + expand */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-space-40 hover:text-space transition-colors flex items-center gap-1"
        >
          {server.tools.length} tools
          <svg
            className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Toggle (not for cowork) */}
        {server.name !== "cowork" && (
          <button
            onClick={handleToggleEnabled}
            disabled={unsupported}
            title={unsupported ? "Not available in the cloud — use this connector on your desktop" : undefined}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
              server.enabled ? "bg-jove" : "bg-space-30"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-surface transition-transform ${
                server.enabled ? "translate-x-4" : "translate-x-1"
              }`}
            />
          </button>
        )}
      </div>

      {/* Action row — wraps on narrow screens; up to six buttons won't fit a
          phone width in a single line. */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
        {!server.builtin && (
          <>
            <Button variant="secondary" size="sm" loading={discovering} onClick={handleDiscover} disabled={unsupported}>
              Discover Tools
            </Button>
            <Button variant="secondary" size="sm" loading={verifying} onClick={handleVerify} disabled={unsupported}>
              Verify
            </Button>
            {(server.transport === "stdio" || server.transport === "mcpb") && (
              <Button variant="secondary" size="sm" loading={restarting} onClick={handleRestart} disabled={unsupported}>
                Restart
              </Button>
            )}
            {onConfigure && (
              <Button variant="secondary" size="sm" onClick={onConfigure}>Configure</Button>
            )}
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <span className="text-hydra">Delete</span>
              </Button>
            )}
          </>
        )}
        {server.builtin && server.tools.length > 0 && (
          <Button variant="secondary" size="sm" loading={discovering} onClick={handleDiscover}>
            Re-discover
          </Button>
        )}
        {message && (
          <p className={`text-xs flex items-center gap-1 ml-auto ${message.ok ? "text-emerald-600" : "text-hydra"}`}>
            {message.ok ? (
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {message.text}
          </p>
        )}
      </div>

      {/* Expanded tool list */}
      {expanded && (
        <div className="border-t border-space-20 px-4 py-3">
          <ToolList tools={server.tools} serverId={server.id} readonly={server.builtin} />
        </div>
      )}
    </div>
  );
}

// ── Add/Edit MCP Server Modal ────────────────────────────────────────

function AddServerModal({
  existing,
  onClose,
  onSaved,
}: {
  existing?: McpServer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addServer, updateServer } = useMcpStore();
  const isEdit = !!existing;

  const [name, setName] = useState(existing?.name ?? "");
  // On the cloud, stdio isn't offered, so default a NEW connector to http there.
  const [transport, setTransport] = useState<string>(
    existing?.transport ?? (isNativeHost() ? "stdio" : "http"),
  );

  // stdio fields
  const [command, setCommand] = useState((existing?.config?.command as string) ?? "");
  const [args, setArgs] = useState((existing?.config?.args as string[])?.join("\n") ?? "");
  // `saved` marks a secret that's stored but redacted here (masked value) → the
  // field is blanked, and a "leave blank to keep" hint is shown; the server keeps
  // the existing secret on save unless a new value is typed.
  const [envPairs, setEnvPairs] = useState<Array<{ key: string; value: string; saved?: boolean }>>(
    existing?.config?.env
      ? Object.entries(existing.config.env as Record<string, string>).map(([key, value]) => {
          const masked = typeof value === "string" && value.includes("...");
          return { key, value: masked ? "" : (value as string), saved: masked };
        })
      : [{ key: "", value: "" }],
  );

  // http/sse fields
  const [url, setUrl] = useState((existing?.config?.url as string) ?? "");
  const [headerPairs, setHeaderPairs] = useState<Array<{ key: string; value: string; saved?: boolean }>>(
    existing?.config?.headers
      ? Object.entries(existing.config.headers as Record<string, string>).map(([key, value]) => {
          const masked = typeof value === "string" && value.includes("...");
          return { key, value: masked ? "" : (value as string), saved: masked };
        })
      : [{ key: "", value: "" }],
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const config: Record<string, unknown> = {};

      if (transport === "stdio") {
        config.command = command.trim();
        config.args = args.split("\n").map((a) => a.trim()).filter(Boolean);
        const env: Record<string, string> = {};
        for (const p of envPairs) {
          if (p.key.trim()) env[p.key.trim()] = p.value;
        }
        if (Object.keys(env).length > 0) config.env = env;
      } else {
        config.url = url.trim();
        const headers: Record<string, string> = {};
        for (const p of headerPairs) {
          if (p.key.trim()) headers[p.key.trim()] = p.value;
        }
        if (Object.keys(headers).length > 0) config.headers = headers;
      }

      if (isEdit && existing) {
        await updateServer(existing.id, { name: name.trim(), transport, config });
      } else {
        await addServer(name.trim(), transport, config);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-space-30">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-space">
              {isEdit ? "Edit MCP Server" : "Add MCP Server"}
            </h3>
            <p className="text-xs text-space-40">
              {isEdit ? "Update server configuration." : "Connect an external MCP server."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-space-40 hover:text-space hover:bg-space-20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Name */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-space">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. github, filesystem, slack"
              className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                         placeholder:text-space-40 transition-all"
            />
          </div>

          {/* Transport */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-space">Transport</label>
            <div className="flex gap-2">
              {/* Local (stdio) connectors can't run in the cloud, so only offer the
                  network transports there — matches the server-side guard. */}
              {(isNativeHost() ? ["stdio", "http", "sse"] : ["http", "sse"]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTransport(t)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    transport === t
                      ? "bg-primary text-on-primary"
                      : "bg-space-20 text-space-50 hover:text-space"
                  }`}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
            {!isNativeHost() && (
              <p className="text-[11px] text-space-40">
                Local (stdio / extension) connectors run on your device — add them from the desktop app.
              </p>
            )}
          </div>

          <hr className="border-space-20" />

          {/* stdio fields */}
          {transport === "stdio" && (
            <>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-space">Command</label>
                <input
                  type="text"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  placeholder="e.g. npx, node, python3"
                  className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                             placeholder:text-space-40 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-space">Arguments</label>
                <textarea
                  value={args}
                  onChange={(e) => setArgs(e.target.value)}
                  placeholder={"One argument per line, e.g.:\n-y\n@modelcontextprotocol/server-github"}
                  rows={3}
                  className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                             placeholder:text-space-40 transition-all resize-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-space">Environment Variables</label>
                {envPairs.map((pair, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={pair.key}
                      onChange={(e) => {
                        const next = [...envPairs];
                        next[i] = { ...pair, key: e.target.value };
                        setEnvPairs(next);
                      }}
                      placeholder="KEY"
                      className="flex-1 px-2 py-1.5 border border-space-30 rounded-lg text-xs font-mono
                                 focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                                 placeholder:text-space-40"
                    />
                    <span className="text-space-40 text-xs">=</span>
                    <SecretInput
                      value={pair.value}
                      onChange={(v) => {
                        const next = [...envPairs];
                        next[i] = { ...pair, value: v };
                        setEnvPairs(next);
                      }}
                      placeholder={pair.saved && !pair.value ? "•••• saved — leave blank to keep" : "value"}
                      className="flex-1"
                    />
                    {envPairs.length > 1 && (
                      <button
                        onClick={() => setEnvPairs(envPairs.filter((_, j) => j !== i))}
                        className="p-1 text-space-40 hover:text-hydra transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setEnvPairs([...envPairs, { key: "", value: "" }])}
                  className="text-xs text-jove hover:text-jove-60 transition-colors"
                >
                  + Add Variable
                </button>
              </div>
            </>
          )}

          {/* http/sse fields */}
          {(transport === "http" || transport === "sse") && (
            <>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-space">URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={transport === "http"
                    ? "https://mcp.example.com/mcp"
                    : "https://mcp.example.com/sse"
                  }
                  className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                             placeholder:text-space-40 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-space">Headers</label>
                {headerPairs.map((pair, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={pair.key}
                      onChange={(e) => {
                        const next = [...headerPairs];
                        next[i] = { ...pair, key: e.target.value };
                        setHeaderPairs(next);
                      }}
                      placeholder="Header-Name"
                      className="flex-1 px-2 py-1.5 border border-space-30 rounded-lg text-xs font-mono
                                 focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                                 placeholder:text-space-40"
                    />
                    <span className="text-space-40 text-xs">:</span>
                    <SecretInput
                      value={pair.value}
                      onChange={(v) => {
                        const next = [...headerPairs];
                        next[i] = { ...pair, value: v };
                        setHeaderPairs(next);
                      }}
                      placeholder={pair.saved && !pair.value ? "•••• saved — leave blank to keep" : "value"}
                      className="flex-1"
                    />
                    {headerPairs.length > 1 && (
                      <button
                        onClick={() => setHeaderPairs(headerPairs.filter((_, j) => j !== i))}
                        className="p-1 text-space-40 hover:text-hydra transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setHeaderPairs([...headerPairs, { key: "", value: "" }])}
                  className="text-xs text-jove hover:text-jove-60 transition-colors"
                >
                  + Add Header
                </button>
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-hydra flex items-start gap-1 whitespace-pre-wrap break-words">
              <svg className="h-3.5 w-3.5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="flex-1">{error}</span>
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-space-30">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            loading={saving}
            onClick={handleSave}
            disabled={!name.trim() || (transport === "stdio" ? !command.trim() : !url.trim())}
          >
            {isEdit ? "Save Changes" : "Add Server"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Delete confirmation modal ────────────────────────────────────────

function DeleteConfirmModal({
  server,
  onClose,
  onConfirm,
}: {
  server: McpServer;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-4">
        <h3 className="text-base font-semibold text-space">Delete MCP Server</h3>
        <p className="text-sm text-space-50">
          Remove <span className="font-medium text-space">{server.name}</span> and
          all its discovered tools? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            loading={deleting}
            onClick={async () => {
              setDeleting(true);
              await onConfirm();
              onClose();
            }}
          >
            <span className="text-white">Delete</span>
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Install MCPB modal ───────────────────────────────────────────────

function InstallMcpbModal({
  onClose,
  onInstalled,
}: {
  onClose: () => void;
  onInstalled: () => void;
}) {
  const { installMcpb, confirmMcpbInstall } = useMcpStore();

  // Step 1: file selection, Step 2: review + config
  const [step, setStep] = useState<"pick" | "configure">("pick");
  const [manifest, setManifest] = useState<McpbManifest | null>(null);
  const [tempDir, setTempDir] = useState("");
  const [loading, setLoading] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User config values
  const [configValues, setConfigValues] = useState<Record<string, string | number | boolean>>({});

  // Initialize defaults when manifest loads
  const initDefaults = (m: McpbManifest) => {
    const defaults: Record<string, string | number | boolean> = {};
    if (m.user_config) {
      for (const [key, field] of Object.entries(m.user_config)) {
        if (field.type === "boolean") {
          defaults[key] = typeof field.default === "boolean" ? field.default : false;
        } else if (field.type === "number") {
          defaults[key] = typeof field.default === "number" ? field.default : 0;
        } else if (field.default !== undefined && typeof field.default === "string") {
          defaults[key] = field.default;
        } else {
          defaults[key] = "";
        }
      }
    }
    setConfigValues(defaults);
  };

  const handlePickFile = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ paths: string[] }>("/files/pick?filter=mcpb");
      if (!res.paths || res.paths.length === 0) {
        setLoading(false);
        return;
      }
      const filePath = res.paths[0]!;
      const result = await installMcpb(filePath);
      setManifest(result.manifest);
      setTempDir(result.temp_dir);
      initDefaults(result.manifest);
      setStep("configure");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async () => {
    setInstalling(true);
    setError(null);
    try {
      await confirmMcpbInstall(tempDir, configValues);
      onInstalled();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInstalling(false);
    }
  };

  const hasUserConfig = manifest?.user_config && Object.keys(manifest.user_config).length > 0;

  // Check if all required fields are filled
  const isValid = () => {
    if (!manifest?.user_config) return true;
    for (const [key, field] of Object.entries(manifest.user_config)) {
      if (field.required && !configValues[key] && configValues[key] !== 0) return false;
    }
    return true;
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-space-30">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-space">
              {step === "pick" ? "Install MCP Extension" : "Configure Extension"}
            </h3>
            <p className="text-xs text-space-40">
              {step === "pick"
                ? "Select a .mcpb or .dxt extension file."
                : "Review and configure the extension before installing."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-space-40 hover:text-space hover:bg-space-20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {step === "pick" && (
            <div className="text-center py-6 space-y-4">
              <div className="text-4xl">📦</div>
              <p className="text-sm text-space-50">
                Choose an MCPB extension file to install.
              </p>
              <Button variant="primary" size="sm" loading={loading} onClick={handlePickFile}>
                Choose File...
              </Button>
            </div>
          )}

          {step === "configure" && manifest && (
            <>
              {/* Extension info card */}
              <div className="rounded-xl border border-space-30 p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="text-3xl shrink-0">📦</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-space truncate">
                        {manifest.display_name || manifest.name}
                      </p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-space-20 text-space-50 font-mono shrink-0">
                        v{manifest.version}
                      </span>
                    </div>
                    <p className="text-xs text-space-50 mt-0.5">{manifest.description}</p>
                    {manifest.author && (
                      <p className="text-xs text-space-40 mt-1">
                        by {manifest.author.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-space-40 pt-1 border-t border-space-20">
                  <span>Type: <span className="font-mono">{manifest.server.type}</span></span>
                  {manifest.tools && manifest.tools.length > 0 && (
                    <span>{manifest.tools.length} tools</span>
                  )}
                  {manifest.license && <span>{manifest.license}</span>}
                </div>
              </div>

              {/* Tool list preview */}
              {manifest.tools && manifest.tools.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-space-40">Tools</p>
                  <div className="rounded-lg bg-space-10 px-3 py-2 space-y-1 max-h-28 overflow-y-auto">
                    {manifest.tools.map((t) => (
                      <p key={t.name} className="text-xs">
                        <span className="font-mono text-space">{t.name}</span>
                        {t.description && (
                          <span className="text-space-40 ml-1.5">— {t.description}</span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* User config form */}
              {hasUserConfig && (
                <div className="space-y-3 pt-1">
                  <hr className="border-space-20" />
                  <p className="text-xs font-medium text-space-40">Configuration</p>
                  {Object.entries(manifest.user_config!).map(([key, field]) => (
                    <UserConfigField
                      key={key}
                      fieldKey={key}
                      field={field}
                      value={configValues[key]}
                      onChange={(v) => setConfigValues((prev) => ({ ...prev, [key]: v }))}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {error && (
            <p className="text-xs text-hydra flex items-center gap-1">
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        {step === "configure" && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-space-30">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={installing}
              onClick={handleInstall}
              disabled={!isValid()}
            >
              Install Extension
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── User config field renderer ───────────────────────────────────────

function UserConfigField({
  fieldKey,
  field,
  value,
  onChange,
}: {
  fieldKey: string;
  field: McpbUserConfigField;
  value: string | number | boolean | undefined;
  onChange: (value: string | number | boolean) => void;
}) {
  const label = field.title || fieldKey;
  const isRequired = field.required;

  if (field.type === "boolean") {
    return (
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-space">
            {label}{isRequired && <span className="text-hydra ml-0.5">*</span>}
          </p>
          {field.description && (
            <p className="text-xs text-space-40 mt-0.5">{field.description}</p>
          )}
        </div>
        <button
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
            value ? "bg-jove" : "bg-space-30"
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-surface transition-transform ${
              value ? "translate-x-4" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-space">
          {label}{isRequired && <span className="text-hydra ml-0.5">*</span>}
        </label>
        {field.description && (
          <p className="text-xs text-space-40">{field.description}</p>
        )}
        <input
          type="number"
          value={value as number ?? ""}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm
                     focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                     placeholder:text-space-40 transition-all"
        />
      </div>
    );
  }

  // string, directory, file
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-space">
        {label}{isRequired && <span className="text-hydra ml-0.5">*</span>}
      </label>
      {field.description && (
        <p className="text-xs text-space-40">{field.description}</p>
      )}
      {field.sensitive ? (
        <SensitiveTextInput
          value={(value as string) ?? ""}
          onChange={(v) => onChange(v)}
          placeholder="Enter secret value"
        />
      ) : (
        <input
          type="text"
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${label.toLowerCase()}`}
          className="w-full px-3 py-2 border border-space-30 rounded-lg text-sm font-mono
                     focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                     placeholder:text-space-40 transition-all"
        />
      )}
    </div>
  );
}

function SensitiveTextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-3 pr-9 py-2 border border-space-30 rounded-lg text-sm font-mono
                   focus:outline-none focus:ring-2 focus:ring-jove-60/30 focus:border-jove-60
                   placeholder:text-space-40 transition-all"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide value" : "Show value"}
        title={visible ? "Hide value" : "Show value"}
        className="absolute inset-y-0 right-2 flex items-center text-space-40 hover:text-space transition-colors"
      >
        {visible ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.243 4.243L9.88 9.88" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        )}
      </button>
    </div>
  );
}

// ── Configure MCPB modal ─────────────────────────────────────────────

function ConfigureMcpbModal({
  server,
  onClose,
  onSaved,
}: {
  server: McpServer;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { getMcpbConfig, updateMcpbConfig } = useMcpStore();
  const [manifest, setManifest] = useState<McpbManifest | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, string | number | boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getMcpbConfig(server.id)
      .then((res) => {
        setManifest(res.manifest);
        // Pre-fill form with current values
        const values: Record<string, string | number | boolean> = {};
        if (res.manifest.user_config) {
          for (const key of Object.keys(res.manifest.user_config)) {
            const current = res.user_config_values[key];
            if (current !== undefined && current !== null) {
              values[key] = current as string | number | boolean;
            } else {
              values[key] = "";
            }
          }
        }
        setConfigValues(values);
        setLoading(false);
      })
      .catch((e) => {
        setError((e as Error).message);
        setLoading(false);
      });
  }, [server.id, getMcpbConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const result = await updateMcpbConfig(server.id, configValues);
      setSuccess(true);
      if (result.restarted) {
        onSaved();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const displayName = (server.config as Record<string, string>).display_name || server.name;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-surface rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-space-30">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-space">Configure {displayName}</h3>
            <p className="text-xs text-space-40">
              Update configuration values. The server will restart automatically.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-space-40 hover:text-space hover:bg-space-20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-space-40">Loading configuration...</p>
          ) : manifest?.user_config ? (
            Object.entries(manifest.user_config).map(([key, field]) => (
              <UserConfigField
                key={key}
                fieldKey={key}
                field={field}
                value={configValues[key]}
                onChange={(v) => setConfigValues((prev) => ({ ...prev, [key]: v }))}
              />
            ))
          ) : (
            <p className="text-xs text-space-40">This extension has no configurable settings.</p>
          )}

          {error && (
            <p className="text-xs text-hydra flex items-center gap-1">
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {error}
            </p>
          )}
          {success && (
            <p className="text-xs text-emerald-600 flex items-center gap-1">
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Configuration saved and server restarted.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-space-30">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" loading={saving} onClick={handleSave}>
            Save & Restart
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Main section component ───────────────────────────────────────────

export function McpServersSection() {
  const { servers, isLoading, loadServers, deleteServer } = useMcpStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [editServer, setEditServer] = useState<McpServer | null>(null);
  const [configureServer, setConfigureServer] = useState<McpServer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<McpServer | null>(null);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const builtinServers = servers.filter((s) => s.builtin);
  const userServers = servers.filter((s) => !s.builtin);

  if (isLoading) {
    return <p className="text-xs text-space-40">Loading MCP servers...</p>;
  }

  return (
    <div className="space-y-6">
      {/* Built-in */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-space">Built-in</h3>
          <p className="text-xs text-space-40 mt-0.5">
            Core servers managed by OmniWerk. Configure via the Connections tab.
          </p>
        </div>
        <div className="space-y-2">
          {builtinServers.map((s) => (
            <ServerCard key={s.id} server={s} />
          ))}
        </div>
      </div>

      {/* User-added */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-space">User Added</h3>
            <p className="text-xs text-space-40 mt-0.5">
              External MCP servers you've connected. Changes take effect on the next conversation turn.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {userServers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-space-30 px-4 py-6 text-center">
              <p className="text-sm text-space-40">No external MCP servers configured</p>
              <p className="text-xs text-space-30 mt-1">
                Add servers to give the agent access to external tools and data sources.
              </p>
            </div>
          ) : (
            userServers.map((s) => (
              <ServerCard
                key={s.id}
                server={s}
                onConfigure={s.transport === "mcpb" ? () => setConfigureServer(s) : undefined}
                onEdit={s.transport !== "mcpb" ? () => setEditServer(s) : undefined}
                onDelete={() => setDeleteTarget(s)}
              />
            ))
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowAddModal(true)}>
            + Add MCP Server
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowInstallModal(true)}>
            Install from File
          </Button>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddServerModal
          onClose={() => setShowAddModal(false)}
          onSaved={loadServers}
        />
      )}
      {showInstallModal && (
        <InstallMcpbModal
          onClose={() => setShowInstallModal(false)}
          onInstalled={loadServers}
        />
      )}
      {configureServer && (
        <ConfigureMcpbModal
          server={configureServer}
          onClose={() => setConfigureServer(null)}
          onSaved={loadServers}
        />
      )}
      {editServer && (
        <AddServerModal
          existing={editServer}
          onClose={() => setEditServer(null)}
          onSaved={loadServers}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          server={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteServer(deleteTarget.id);
          }}
        />
      )}
    </div>
  );
}
